import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid4 } from 'uuid';
import TextButton from '@design-system/text-button';
import PhaseProgress from '@design-system/phase-progress';
import VocabularyContent from '@component/iyagigo/worksheet/vocabulary-content';
import PatternContent from '@component/iyagigo/worksheet/pattern-content';
import ClozeContent from '@component/iyagigo/worksheet/cloze-content';
import SpeakingContent from '@component/iyagigo/worksheet/speaking-content';
import SentenceContent from '@component/iyagigo/worksheet/sentence-content';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import BottomContainer from '@component/common/architecture/bottom-container';
import { useTodayWorksheetWorkbookQuery, useModifyWorksheetWorkbookMutation } from '@hook/iyagigo/useApi';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useRecordStudy } from '@hook/iyagigo/useStudent';
import { useActiveTime } from '@hook/common/useEvent';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import {
  EApiLanguageCode,
  EApiStudyType,
  IWorksheetWorkbookData,
  TWorksheetStudyContent,
} from '@datatype/iyagigo/api-type';
import { ETheme } from '@datatype/type';
import { completeSentence } from '@util/string';
import { sanitizeSentence } from '@util/workbook';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import Gap from '@component/common/gap';

type TStudyPhase = 'sentence' | 'vocabulary' | 'pattern' | 'cloze' | 'speaking';
const PHASES: { id: string; phase: TStudyPhase; label: string; title: string; description: string }[] = [
  {
    id: uuid4(),
    phase: 'sentence',
    label: '문장',
    title: '오늘의 문장',
    description: '문장을 읽고, 어떤 뜻인지 생각해 보세요.',
  },
  {
    id: uuid4(),
    phase: 'vocabulary',
    label: '어휘',
    title: '문장 속 어휘',
    description: '어휘의 뜻을 떠올린 뒤 확인해 보세요.',
  },
  {
    id: uuid4(),
    phase: 'pattern',
    label: '문형',
    title: '문형 살펴보기',
    description: '이 문장에서 쓰인 문형의 뜻과 쓰임을 알아봐요.',
  },
  {
    id: uuid4(),
    phase: 'cloze',
    label: '빈칸문제',
    title: '문장 완성하기',
    description: '뜻에 맞게 보기의 조각을 순서대로 골라보세요.',
  },
  {
    id: uuid4(),
    phase: 'speaking',
    label: '말하기',
    title: '대화 따라 읽기',
    description: '대화를 듣고, 내 차례에는 소리 내어 읽어 보세요.',
  },
];
const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  height: 100%;
  min-height: 0;
  position: relative;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0 ${size.PAGE_PADDING};
`;
const FixContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-shrink: 0;
  width: 100%;
`;
const PhaseProgressContainer = styled.div`
  width: 100%;
  padding: 10px 0 5px 0;
  border: ${size.BORDER_WIDTH_1} solid ${color.BROWN_3};
  border-radius: ${size.BORDER_RADIUS_3};
  background-color: ${color.BROWN_5};
`;
const ContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;
const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${size.MARGIN_1};
  width: 100%;
  padding: 0 ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;
const PRIMARY_BUTTON = { theme: ETheme.BROWN, size: 'medium', shape: 'round', width: '100%' } as const;
const Worksheet: React.FC = () => {
  const studyLanguage =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? EApiLanguageCode.JAPANESE;
  const query = useTodayWorksheetWorkbookQuery(studyLanguage);
  const workbook = query.data?.data;
  if (!workbook) return <div />;
  return <WorksheetStudy key={workbook.key} workbook={workbook} />;
};

const WorksheetStudy: React.FC<{ workbook: IWorksheetWorkbookData }> = ({ workbook }) => {
  const dispatch = useTypedDispatch();
  const worksheet = workbook.worksheet;
  const source = useMemo<TWorksheetStudyContent>(
    () => ({ ...worksheet.content, key: worksheet.key, languageCode: worksheet.languageCode }),
    [worksheet],
  );
  const [phase, setPhase] = useState<TStudyPhase>('sentence');
  const [isCompleted, setIsCompleted] = useState(false);
  const modifyWorkbookMutation = useModifyWorksheetWorkbookMutation(worksheet.languageCode);
  const handleComplete = () => {
    if (modifyWorkbookMutation.isPending) return;
    modifyWorkbookMutation.mutate(
      { workbookKey: workbook.key, studentKey: localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '', isStudy: true },
      {
        onSuccess: () => {
          setCompletedPhases((items) => (items.includes(phase) ? items : [...items, phase]));
          setIsCompleted(true);
          dispatch(
            modalActions.showContentModal({
              type: 'study-celebration',
              data: {
                title: '오늘도 해냈어요!',
                description: '<strong>알짜 학습지</strong> 학습을 끝까지 마쳤어요',
              },
            }),
          );
        },
      },
    );
  };
  const [completedPhases, setCompletedPhases] = useState<TStudyPhase[]>([]);
  const [visitedPhases, setVisitedPhases] = useState<TStudyPhase[]>(['sentence']);
  const advancePhase = (nextPhase: TStudyPhase) => {
    setCompletedPhases((items) => (items.includes(phase) ? items : [...items, phase]));
    setVisitedPhases((items) => (items.includes(nextPhase) ? items : [...items, nextPhase]));
    setIsCompleted(false);
    setPhase(nextPhase);
  };
  const recordStudy = useRecordStudy();
  const { getActiveTime, reset: resetActiveTime } = useActiveTime(!isCompleted);
  const previousPhaseRef = useRef(phase);
  const completionRecordedRef = useRef(false);

  useEffect(() => {
    if (previousPhaseRef.current === phase && (!isCompleted || completionRecordedRef.current)) return;
    previousPhaseRef.current = phase;
    const studyTime = getActiveTime();
    const studyCount = isCompleted && !completionRecordedRef.current ? 1 : undefined;
    if (studyCount) completionRecordedRef.current = true;
    if (studyTime > 0 || studyCount) {
      recordStudy({
        studentKey: localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '',
        studyType: EApiStudyType.WORKSHEET,
        languageCode: source.languageCode,
        studyTime,
        ...(studyCount ? { studyCount } : {}),
      });
    }
    resetActiveTime();
  }, [phase, isCompleted, source, recordStudy, getActiveTime, resetActiveTime]);

  const [vocabularyIndex, setVocabularyIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [sentenceRevealed, setSentenceRevealed] = useState(false);
  const [selected, setSelected] = useState<{ id: string; word: string; idx: number }[]>([]);
  const [answered, setAnswered] = useState(false);
  const conversation = source.conversationKo ?? null;
  const hasSpeaking = !!(source.sourceConversation?.line?.length && conversation?.line?.length);
  const studyPhases = PHASES.filter((item) => item.phase !== 'speaking' || hasSpeaking);
  const finishCloze = () => {
    if (hasSpeaking) advancePhase('speaking');
    else handleComplete();
  };
  const phaseInfo = PHASES.find((item) => item.phase === phase);
  const sentence = completeSentence(
    source.languageCode === EApiLanguageCode.ENGLISH ? source.sentenceTemplateEn : source.sentenceTemplateJa,
    source.languageCode === EApiLanguageCode.ENGLISH ? source.answerEn : source.answerJa,
    false,
  );
  // 보조해설의 어휘와 UI key를 원본별로 한 번 구성한다.
  const vocabularyItems = useMemo(() => {
    const seen = new Set<string>();
    return (source.addExplanationKo ?? [])
      .filter((item) => {
        if (!item.keyword || !item.meaning?.length || seen.has(item.keyword)) return false;
        seen.add(item.keyword);
        return true;
      })
      .slice(0, 3)
      .map((item) => ({ ...item, id: uuid4() }));
  }, [source]);
  const currentVocabulary = vocabularyItems[vocabularyIndex];
  const correct = sanitizeSentence(selected.map((item) => item.word).join('')) === sanitizeSentence(sentence);

  return (
    <Wrapper>
      <FixContainer>
        <PhaseProgressContainer>
          <PhaseProgress
            size='small'
            items={studyPhases}
            currentId={studyPhases.find((item) => item.phase === phase)?.id ?? ''}
            completed={isCompleted}
            visitedIds={studyPhases.filter((item) => visitedPhases.includes(item.phase)).map((item) => item.id)}
            completedIds={studyPhases.filter((item) => completedPhases.includes(item.phase)).map((item) => item.id)}
            handlePhaseClick={(id) => {
              const target = studyPhases.find((item) => item.id === id);
              if (!target || target.phase === phase) return;
              const latestCompletedIndex = studyPhases.reduce(
                (latest, item, index) => (completedPhases.includes(item.phase) ? Math.max(latest, index) : latest),
                -1,
              );
              if (studyPhases.indexOf(target) > latestCompletedIndex + 1) return;
              setIsCompleted(false);
              setPhase(target.phase);
            }}
            enabledIds={studyPhases
              .filter((item, index) => index === 0 || completedPhases.includes(studyPhases[index - 1].phase))
              .map((item) => item.id)}
            width='100%'
          />
        </PhaseProgressContainer>
      </FixContainer>
      <Gap height={size.MARGIN_1} />
      {(phase === 'sentence' || phase === 'vocabulary' || phase === 'pattern' || phase === 'cloze') && (
        <ScrollContainer style={{ margin: `0 -${size.PAGE_PADDING}` }}>
          <Scroll
            key={phase}
            direction='y'
            height='100%'
            padding={{ left: size.PAGE_PADDING, right: size.PAGE_PADDING }}>
            <ContentContainer>
              {phase === 'sentence' && (
                <SentenceContent
                  translationVisible={sentenceRevealed}
                  handleReveal={() => setSentenceRevealed(true)}
                  title={phaseInfo!.title}
                  description={phaseInfo!.description}
                  source={source}
                  sentence={sentence}
                  vocabularyItems={vocabularyItems}
                />
              )}
              {phase === 'vocabulary' && currentVocabulary && (
                <VocabularyContent
                  title={phaseInfo!.title}
                  description={phaseInfo!.description}
                  key={currentVocabulary.id}
                  vocabulary={currentVocabulary}
                  current={vocabularyIndex + 1}
                  total={vocabularyItems.length}
                  revealed={revealed}
                  handleReveal={() => setRevealed(true)}
                />
              )}
              {phase === 'pattern' && (
                <PatternContent
                  pattern={source.answerExplanationKo!}
                  title={phaseInfo!.title}
                  description={phaseInfo!.description}
                />
              )}
              {phase === 'cloze' && (
                <ClozeContent
                  source={source}
                  sentence={sentence}
                  answered={answered}
                  correct={correct}
                  selected={selected}
                  handleSelect={(word, idx) => setSelected((items) => [...items, { id: uuid4(), word, idx }])}
                  handleRemove={(id) => setSelected((items) => items.filter((item) => item.id !== id))}
                  handleCheck={() => setAnswered(true)}
                  handleRetry={() => {
                    setSelected([]);
                    setAnswered(false);
                  }}
                  title={phaseInfo!.title}
                  description={phaseInfo!.description}
                />
              )}
            </ContentContainer>
          </Scroll>
        </ScrollContainer>
      )}
      {phase === 'speaking' && source.sourceConversation && conversation && (
        <ScrollContainer style={{ margin: `0 -${size.PAGE_PADDING}` }}>
          <SpeakingContent
            workbookKey={workbook.key}
            languageCode={source.languageCode}
            sourceConversation={source.sourceConversation}
            conversation={conversation}
            conversationAudioLinks={source.conversationAudioLink}
            handleCompleteClick={handleComplete}
          />
        </ScrollContainer>
      )}
      {phase !== 'speaking' && (
        <>
          <Gap height={size.MARGIN_1} />
          <BottomContainer
            position='relative'
            height='auto'
            backgroundColor={color.WHITE_1}
            margin={`0 -${size.PAGE_PADDING}`}>
            <BottomContentContainer>
              {phase === 'sentence' && (
                <TextButton
                  {...PRIMARY_BUTTON}
                  disabled={!sentenceRevealed}
                  text='어휘 보기'
                  handleClick={() => advancePhase('vocabulary')}
                />
              )}
              {phase === 'vocabulary' && (
                <TextButton
                  {...PRIMARY_BUTTON}
                  text={vocabularyIndex + 1 < vocabularyItems.length ? '다음 어휘' : '문형 보기'}
                  disabled={!revealed}
                  handleClick={() => {
                    if (!revealed) return;
                    if (vocabularyIndex + 1 < vocabularyItems.length) {
                      setVocabularyIndex((value) => value + 1);
                      setRevealed(false);
                      return;
                    }
                    advancePhase('pattern');
                  }}
                />
              )}
              {phase === 'pattern' && (
                <TextButton {...PRIMARY_BUTTON} text='빈칸문제 풀기' handleClick={() => advancePhase('cloze')} />
              )}
              {phase === 'cloze' && (
                <TextButton
                  {...PRIMARY_BUTTON}
                  disabled={!answered || modifyWorkbookMutation.isPending}
                  text={hasSpeaking ? '말하기' : '오늘 학습 마치기'}
                  handleClick={finishCloze}
                />
              )}
            </BottomContentContainer>
          </BottomContainer>
        </>
      )}
    </Wrapper>
  );
};

export default Worksheet;
